import { costFormater } from '@common-fe/common-fe';
import isNaN from 'lodash/isNaN';
import toNumber from 'lodash/toNumber';
import toString from 'lodash/toString';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import { MUST_BE_GREATER_THAN_ZERO_TEXT, REQUIRED_TEXT } from '@/common/constants';
import { TriggerCategory } from '@/modules/HealthPlan/HealthPlan.types';
import {
  RenewalAutoFundingConfig,
  RenewalDeductibleConfig,
  RenewalProcessingTier,
  RenewalTriggerConfig,
} from '@/modules/Renewals/renewals.types';

// --- Types ---

interface TierWithTriggers {
  tier: RenewalProcessingTier;
}

const TRIGGER_AMOUNT_TEST_NAME = 'trigger-amount';
const AUTO_FUNDING_AMOUNT_TEST_NAME = 'auto-funding-amount';

// --- Validation Helpers ---

/** Required + numeric check */
const validateRequired = (val: string | undefined): string | null => {
  if (!val || val.trim() === '') return REQUIRED_TEXT;
  if (isNaN(toNumber(val))) return REQUIRED_TEXT;
  return null;
};

/** Cross-tier: current trigger must be bigger than closest filled previous tier trigger of same category */
const validateCrossTier = (
  numVal: number,
  currentTriggerMeta: RenewalTriggerConfig,
  allTriggerAmounts: string[],
  allTriggerMetas: RenewalTriggerConfig[],
  currentTierOrder: number,
  tiersWithTriggers: TierWithTriggers[]
): string | null => {
  for (let prevOrder = currentTierOrder - 1; prevOrder >= 0; prevOrder--) {
    const prevTierIds = tiersWithTriggers[prevOrder].tier.tierTriggerIds || [];

    const prevTriggerIdx = allTriggerMetas.findIndex((meta) => {
      const id = toNumber(meta.triggerId);
      return prevTierIds.includes(id) && meta.triggerCategoryType === currentTriggerMeta.triggerCategoryType;
    });

    if (prevTriggerIdx === -1) continue;

    const prevVal = allTriggerAmounts[prevTriggerIdx];
    if (prevVal && toString(prevVal).trim() !== '') {
      const prevAmount = toNumber(prevVal);
      if (!isNaN(prevAmount) && numVal <= prevAmount) {
        const prevName = allTriggerMetas[prevTriggerIdx].triggerName || `Tier ${prevOrder + 1}`;
        return `Should be bigger than ${prevName}`;
      }
    }
    break;
  }
  return null;
};

/** Within-tier: EMBEDDED must be ≤ FAMILY */
const validateEmbeddedVsFamily = (
  numVal: number,
  currentTriggerMeta: RenewalTriggerConfig,
  allTriggerAmounts: string[],
  allTriggerMetas: RenewalTriggerConfig[],
  currentTierIds: number[]
): string | null => {
  if (currentTriggerMeta.triggerCategoryType !== TriggerCategory.EMBEDDED) return null;

  const familyIdx = allTriggerMetas.findIndex((meta) => {
    const id = toNumber(meta.triggerId);
    return currentTierIds.includes(id) && meta.triggerCategoryType === TriggerCategory.FAMILY;
  });

  if (familyIdx !== -1) {
    const familyVal = allTriggerAmounts[familyIdx];
    if (familyVal && toString(familyVal).trim() !== '') {
      const familyAmount = toNumber(familyVal);
      if (!isNaN(familyAmount) && numVal > familyAmount) {
        const familyName = allTriggerMetas[familyIdx].triggerName || 'Family trigger';
        return `Should be equal or less than ${familyName}`;
      }
    }
  }
  return null;
};

const validateAutoFundingAmount = (val?: string, fundingConfig?: RenewalAutoFundingConfig): string | null => {
  if (!val || val.trim() === '') return REQUIRED_TEXT;

  const numVal = toNumber(val);
  if (isNaN(numVal)) return REQUIRED_TEXT;

  const min = fundingConfig?.minElectionAmount;
  const max = fundingConfig?.maxElectionAmount;

  if (min != null && max != null && (numVal < min || numVal > max)) {
    return `Should be in range from ${costFormater(min, true)} to ${costFormater(max, true)}`;
  }

  return null;
};

// --- Schema Builder ---

export const buildEditAmountsSchema = (
  processingTiers: RenewalProcessingTier[],
  deductibleConfigs?: RenewalDeductibleConfig[]
) => {
  const tiersWithTriggers: TierWithTriggers[] = processingTiers
    .map((tier) => ({ tier }))
    .filter(({ tier }) => tier.tierTriggerIds && tier.tierTriggerIds.length > 0);

  const triggerToTierOrder = new Map<number, number>();
  tiersWithTriggers.forEach(({ tier }, order) => {
    (tier.tierTriggerIds || []).forEach((id) => triggerToTierOrder.set(id, order));
  });

  // Build flat trigger metadata per coverage level for cross-reference
  const coverageTriggerMetas: RenewalTriggerConfig[][] = (deductibleConfigs || []).map(
    (config) => config.triggerConfigs || []
  );

  // Build flat auto-funding metadata per coverage level (same order as the form plans)
  const coverageFundingMetas: RenewalAutoFundingConfig[][] = (deductibleConfigs || []).map(
    (config) => config.autoFundingConfigs || []
  );

  return yup.object({
    tiers: yup.array().of(
      yup.object({
        services: yup.array().of(
          yup.object({
            paymentRules: yup.array().of(
              yup.object({
                amount: VALIDATORS.REQUIRED_STRING,
              })
            ),
          })
        ),
      })
    ),
    coverageLevels: yup.array().of(
      yup.object({
        triggers: yup.array().of(
          yup.object({
            amount: yup.string().test({
              name: TRIGGER_AMOUNT_TEST_NAME,
              test(val) {
                const requiredError = validateRequired(val);
                if (requiredError) return this.createError({ message: requiredError });

                const numVal = toNumber(val);

                // Must be greater than $0
                if (numVal <= 0) {
                  return this.createError({ message: MUST_BE_GREATER_THAN_ZERO_TEXT });
                }

                // Determine coverage level index and trigger index via this.from (parent references)
                const triggerObj = this.from?.[0]?.value;
                const coverageLevel = this.from?.[1]?.value;
                const rootValue = this.from?.[2]?.value;

                const allTriggers = coverageLevel?.triggers || [];
                const triggerIdx = allTriggers.indexOf(triggerObj);

                const allCoverageLevels = rootValue?.coverageLevels || [];
                const coverageIdx = allCoverageLevels.indexOf(coverageLevel);

                if (coverageIdx < 0 || triggerIdx < 0) return true;

                const triggerMetas = coverageTriggerMetas[coverageIdx] || [];
                const currentTriggerMeta = triggerMetas[triggerIdx];
                if (!currentTriggerMeta) return true;

                const currentTierOrder = triggerToTierOrder.get(Number(currentTriggerMeta.triggerId));
                if (currentTierOrder == null) return true;

                // Get all trigger amounts from the coverage level
                const allTriggerAmounts: string[] = allTriggers.map((t: { amount: string }) => t.amount);

                const crossTierError = validateCrossTier(
                  numVal,
                  currentTriggerMeta,
                  allTriggerAmounts,
                  triggerMetas,
                  currentTierOrder,
                  tiersWithTriggers
                );
                if (crossTierError) return this.createError({ message: crossTierError });

                const embeddedError = validateEmbeddedVsFamily(
                  numVal,
                  currentTriggerMeta,
                  allTriggerAmounts,
                  triggerMetas,
                  tiersWithTriggers[currentTierOrder].tier.tierTriggerIds || []
                );
                if (embeddedError) return this.createError({ message: embeddedError });

                return true;
              },
            }),
          })
        ),
        plans: yup.array().of(
          yup.object({
            perPersonSpendLimit: VALIDATORS.POSITIVE_NUMBER_ONLY,
            categorySpendLimits: yup.array().of(
              yup.object({
                amount: VALIDATORS.POSITIVE_NUMBER_ONLY,
              })
            ),
            autoFundingAmount: yup.string().test({
              name: AUTO_FUNDING_AMOUNT_TEST_NAME,
              test(val) {
                // The field is only rendered/validated for plans that expose an
                // auto-funding amount; skip when it is not present at all.
                if (val === undefined) return true;

                // Resolve coverage level and plan indices via the parent chain.
                const planObj = this.from?.[0]?.value;
                const coverageLevel = this.from?.[1]?.value;
                const rootValue = this.from?.[2]?.value;

                const allCoverageLevels = rootValue?.coverageLevels || [];
                const coverageIdx = allCoverageLevels.indexOf(coverageLevel);
                const allPlans = coverageLevel?.plans || [];
                const planIdx = allPlans.indexOf(planObj);

                const fundingConfig =
                  coverageIdx >= 0 && planIdx >= 0 ? coverageFundingMetas[coverageIdx]?.[planIdx] : undefined;

                const error = validateAutoFundingAmount(val, fundingConfig);
                if (error) return this.createError({ message: error });

                return true;
              },
            }),
          })
        ),
      })
    ),
  });
};
